const features = [
  {
    title: "Intelligent Data Extraction",
    desc: "Automatically extract key data from customer POs including line items, quantities, prices, and delivery terms with 99%+ accuracy.",
    icon: "/images/ai/data-extraction.png",
  },
  {
    title: "Multi-Format PO Processing",
    desc: "Process purchase orders in various formats including PDF, scanned images, Excel, and email attachments seamlessly.",
    icon: "/images/ai/multi-format.png",
  },
  {
    title: "Automated Sales Order Creation",
    desc: "Generate sales orders directly in your ERP system without manual intervention, reducing processing time from hours to minutes.",
    icon: "/images/ai/sales-order-creation.png",
  },
  {
    title: "AI-Powered Validation",
    desc: "Intelligent validation checks for data accuracy, pricing consistency, and inventory availability before order creation.",
    icon: "/images/ai/validation.png",
  },
  {
    title: "Seamless ERP Integration",
    desc: "Direct integration with major ERP systems including SAP, Oracle, Microsoft Dynamics, and custom enterprise solutions.",
    icon: "/images/ai/erp-integration.png",
  },
  {
    title: "Processing Analytics & Insights",
    desc: "Comprehensive dashboards showing processing metrics, accuracy rates, and time savings with actionable insights.",
    icon: "/images/ai/analytics.png",
  },
];

export function Features() {
  return (
    <div className="py-16 bg-gradient-to-b from-gray-50 to-white">
      <div className="max-w-7xl mx-auto px-6">
        <div className="text-center mb-16">
          <h2 className="text-4xl font-bold bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent mb-4">
            AI PO Processing Features
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            Advanced AI capabilities that transform manual purchase order processing into automated, intelligent workflows
          </p>
        </div>

        <div className="grid gap-8 md:grid-cols-2 lg:grid-cols-3">
          {features.map((feature, i) => (
            <div
              key={i}
              className="bg-white rounded-2xl overflow-hidden shadow-lg hover:shadow-2xl transform hover:-translate-y-2 transition-all duration-300 group border border-gray-100"
            >
              {/* Image Container with AI-themed Gradient Overlay */}
              <div className="relative h-48 overflow-hidden">
                <div
                  className="h-full bg-cover bg-center group-hover:scale-110 transition-transform duration-500"
                  style={{ backgroundImage: `url(${feature.icon})` }}
                ></div>
                <div className="absolute inset-0 bg-gradient-to-br from-blue-500/10 to-purple-600/10 opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                <div className="absolute top-4 left-4">
                  <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-purple-600 rounded-full flex items-center justify-center shadow-lg">
                    <span className="text-white font-bold text-sm">{i + 1}</span>
                  </div>
                </div>
                {/* AI Badge */}
                <div className="absolute top-4 right-4">
                  <span className="bg-gradient-to-r from-green-500 to-emerald-600 text-white text-xs px-3 py-1 rounded-full font-medium shadow-lg">
                    AI Powered
                  </span>
                </div>
              </div>
              
              {/* Content */}
              <div className="p-6">
                <h3 className="text-xl font-bold text-gray-800 mb-3 group-hover:text-blue-600 transition-colors">
                  {feature.title}
                </h3>
                <p className="text-gray-600 leading-relaxed">{feature.desc}</p>
                
                {/* Feature Tags */}
                <div className="mt-4 flex flex-wrap gap-2">
                  {feature.title.includes("Extraction") && (
                    <span className="text-xs bg-blue-100 text-blue-800 px-3 py-1 rounded-full font-medium">
                      OCR Technology
                    </span>
                  )}
                  {feature.title.includes("Multi-Format") && (
                    <span className="text-xs bg-green-100 text-green-800 px-3 py-1 rounded-full font-medium">
                      Multi-Format
                    </span>
                  )}
                  {feature.title.includes("Sales Order") && (
                    <span className="text-xs bg-purple-100 text-purple-800 px-3 py-1 rounded-full font-medium">
                      Automation
                    </span>
                  )}
                  {feature.title.includes("Validation") && (
                    <span className="text-xs bg-orange-100 text-orange-800 px-3 py-1 rounded-full font-medium">
                      AI Validation
                    </span>
                  )}
                  {feature.title.includes("ERP") && (
                    <span className="text-xs bg-red-100 text-red-800 px-3 py-1 rounded-full font-medium">
                      Integration
                    </span>
                  )}
                  {feature.title.includes("Analytics") && (
                    <span className="text-xs bg-indigo-100 text-indigo-800 px-3 py-1 rounded-full font-medium">
                      Insights
                    </span>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Additional AI Capabilities Section */}
        <div className="mt-16 bg-gradient-to-r from-blue-50 to-purple-50 rounded-2xl p-8 border border-blue-100">
          <h3 className="text-2xl font-bold text-center mb-6 text-gray-800">Advanced AI Capabilities</h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 text-center">
            <div className="bg-white rounded-xl p-6 shadow-md">
              <div className="text-3xl mb-3">🎯</div>
              <h4 className="font-bold text-gray-800 mb-2">99%+ Accuracy</h4>
              <p className="text-gray-600 text-sm">High precision data extraction with machine learning validation</p>
            </div>
            <div className="bg-white rounded-xl p-6 shadow-md">
              <div className="text-3xl mb-3">⚡</div>
              <h4 className="font-bold text-gray-800 mb-2">10x Faster</h4>
              <p className="text-gray-600 text-sm">Reduce processing time from hours to minutes</p>
            </div>
            <div className="bg-white rounded-xl p-6 shadow-md">
              <div className="text-3xl mb-3">🤖</div>
              <h4 className="font-bold text-gray-800 mb-2">Self-Learning</h4>
              <p className="text-gray-600 text-sm">Continuously improves accuracy with more data</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}